import React, { useContext, useEffect, useRef, useState } from "react";
import { AttributeContext } from "../AttributeContext";
import EditAttValues from './EditAttValues'
import Accordion from "Components/DesignSystem/Accordion/Accordion";
import Skeleton from "Components/DesignSystem/Skeleton/Skeleton";

import { listAttributeValuesByAttributeID } from "api/Attributes";

/**
 * Edit-mode "Attribute Values" accordion. Fetch logic (aDetail fast path on the
 * first expand, append-vs-reset pagination, the editingItem reset) is verbatim;
 * only the rendering moved onto DS Accordion + Alert. Note `initialLoading` is a
 * ref OBJECT in the loading condition — always truthy — a legacy quirk kept as-is.
 */
const AttributeValues = ({
  aDetail,
  finalAttributeValues,
  setFinalAttributeValues,
  attributeValueCount
}) => {
  const { editingItem, attributeLoading, setAttributeLoading } = useContext(AttributeContext);
  /*
    § 288 — the SEARCH half of `loading` below: that one status covers the search
    fetch AND the view-more append, and the field must not claim a search when the
    user clicked "View More". Presentational; passed down and read nowhere else.
  */
  const [searchLoading, setSearchLoading] = useState(false);
  const [loading, setLoading] = useState({
    status: "",
    message: "",
  });

  const [attValues, setAttValues] = useState(null);
  const [attValuesCount, setAttValuesCount,] = useState(attributeValueCount);
  const [collapse, setCollapse] = useState(false);
  const [search, setSearch] = useState(undefined)
  const initialLoading = useRef(true)

  useEffect(() => {
    if (collapse && attValues === null) {
      initialLoading.current = true;
      load_attribute_values().then((r) => initialLoading.current = false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collapse]);

  useEffect(() => {
    setLoading({
      status: "",
      message: "",
      message2: "",
    });
    setAttValues(null)
    setAttValuesCount(attributeValueCount ?? 0)
    setFinalAttributeValues([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingItem.id])


  function handle_collapse() {
    setCollapse(!collapse);
  }

  async function load_attribute_values(search, skip, reset) {
    const limit = skip !== undefined ? 10 + skip : 10;
    if(reset) setAttValues(null)
    setAttributeLoading(true)
    setLoading({ status: "Loading", message: "Loading Attribute values" });
    /* § 288 — a search/first load, not a view-more append: `skip === undefined` is the
       discriminator this function already takes, and `loading` below cannot tell the
       two apart. Cleared on every exit, including the cached-detail early return. */
    if (skip === undefined) setSearchLoading(true)
    if (aDetail && initialLoading.current) {
      setAttValues(aDetail.data);
      setLoading({ status: "Success", message: undefined });
      setAttributeLoading(false)
      setSearchLoading(false)
      return
    }

    listAttributeValuesByAttributeID(editingItem.id, limit, skip, search)
    .then(({attribute_values, count}) => {
      setAttValuesCount(count)
      setAttValues((prev) => {
        return prev?.length > 0 && !reset ? [...prev, ...attribute_values] : attribute_values
      })
      setLoading({ status: "Success", message: undefined });
    })
    .catch(() => {
      setLoading({ status: "Error", message: "Error while loading details" });

      setTimeout(() => {
        setLoading({
          status: "Error",
          message: undefined,
        });
      }, 2000)
    }).finally(()=>{ setAttributeLoading(false); setSearchLoading(false) })

  }

  useEffect(() => {
    if(initialLoading.current) return
    load_attribute_values(search, undefined, true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search])

  return (
    <Accordion
      title="Attribute Values"
      open={collapse}
      onOpenChange={() => handle_collapse()}
      data-testid="componets-attributevalues-div-1"
      variant="boxed"
    >
      <div className="tw-pb-4">
        {/* Skeleton chips for a load with nothing on screen yet — the
            module-wide loading grammar (user-requested 2026-09-01). While values
            are already visible (a view-more append), EditAttValues shows an
            inline spinner in the button's place instead, and nothing renders
            here. (`initialLoading` is the ref object — always truthy — a legacy
            quirk kept as-is.) */}
        {initialLoading && loading.status === "Loading" && !attValues && (
          <div
            className="tw-flex tw-flex-wrap tw-items-center tw-gap-2"
            data-testid="attribute-values-skeleton"
          >
            {[72, 96, 56, 88, 64, 80].map((w, i) => (
              <Skeleton key={i} variant="circle" width={`${w}px`} height="26px" />
            ))}
          </div>
        )}

        {attValues && (
          <div className="tw-border tw-border-edge tw-rounded-ctl tw-overflow-hidden">
            <EditAttValues
              attValues={attValues}
              setAttValues={setAttValues}
              loading={loading}
              setLoading={setLoading}
              finalAttributeValues={finalAttributeValues}
              setFinalAttributeValues={setFinalAttributeValues}
              fetchAttributeValues={load_attribute_values}
              attValuesCount={attValuesCount}
              search={search}
              setSearch={setSearch}
              searchLoading={searchLoading}
            />
          </div>
        )}
      </div>
    </Accordion>
  );
};

export default AttributeValues;
